import type {ChatState, Turn} from '@microsoft/agent-host-protocol'
import {getTurns} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  CONTRIBUTED_INSTRUCTIONS_META_KEY,
  SPACE_META_KEY,
  type AhpSpaceSelection,
  type ContributedInstructionKind,
  type ContributedInstructionMetadata,
  type ContributedInstructionsMetadata,
} from '@github-ui/ahp-session-client/contracts'

export type ShouldShowUserMessage = (message: Turn['message'] | undefined) => boolean

export const showAllUserMessages: ShouldShowUserMessage = () => true

/**
 * The per-turn actor carried on an AHP message's `_meta["github.actor"]`
 */
export interface TurnActor {
  githubId: number
}

const ACTOR_META_KEY = 'github.actor'
const MESSAGE_TYPE_META_KEY = 'github.messageType'
const MAX_CONTRIBUTED_INSTRUCTIONS = 32
const MAX_CONTRIBUTED_INSTRUCTION_STRING_LENGTH = 512
const MAX_CONTRIBUTED_INSTRUCTIONS_METADATA_LENGTH = 8_192
const MAX_SPACE_NAME_LENGTH = 512

export type TurnMessageType = 'user' | 'system'

/**
 * Tolerantly decodes a `github.actor` blob; malformed input yields `undefined`
 * rather than throwing.
 */
export function decodeTurnActor(raw: unknown): TurnActor | undefined {
  if (raw == null || typeof raw !== 'object') return undefined
  const {githubId} = raw as Record<string, unknown>
  if (typeof githubId !== 'number' || !Number.isSafeInteger(githubId) || githubId <= 0) {
    return undefined
  }
  return {githubId}
}

/** Reads and decodes the actor carried on a turn's initiating message, if any. */
export function extractTurnActor(message: Turn['message'] | undefined): TurnActor | undefined {
  return decodeTurnActor(message?._meta?.[ACTOR_META_KEY])
}

export function decodeTurnMessageType(raw: unknown): TurnMessageType | undefined {
  return raw === 'user' || raw === 'system' ? raw : undefined
}

export function extractTurnMessageType(message: Turn['message'] | undefined): TurnMessageType | undefined {
  return decodeTurnMessageType(message?._meta?.[MESSAGE_TYPE_META_KEY])
}

const CONTRIBUTED_INSTRUCTION_KINDS: ReadonlySet<ContributedInstructionKind> = new Set([
  'personal',
  'repository',
  'organization',
])

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function boundedString(value: unknown, maxLength = MAX_CONTRIBUTED_INSTRUCTION_STRING_LENGTH): string | undefined {
  return typeof value === 'string' && value.length > 0 && value.length <= maxLength ? value : undefined
}

export function extractTurnSpace(message: Turn['message'] | undefined): AhpSpaceSelection | undefined {
  try {
    const raw = message?._meta?.[SPACE_META_KEY]
    if (!isPlainRecord(raw)) return undefined
    const {owner, id, slug} = raw
    const name = boundedString(raw.name, MAX_SPACE_NAME_LENGTH)
    if (
      typeof owner !== 'string' ||
      owner.length < 1 ||
      owner.length > 39 ||
      /[^a-z0-9_-]/i.test(owner) ||
      typeof id !== 'number' ||
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      typeof slug !== 'string' ||
      !name?.trim()
    ) {
      return undefined
    }
    return {owner, id, slug, name}
  } catch {
    return undefined
  }
}

function safeInstructionUrl(
  value: unknown,
  kind: ContributedInstructionKind,
  label: string | undefined,
): string | undefined {
  const url = boundedString(value)
  if (!url || !label || kind === 'personal' || !URL.canParse(url)) return undefined
  try {
    const parsed = new URL(url, 'https://github.com')
    const expectedPath = `/${label}`.toLowerCase()
    const path = parsed.pathname.replace(/\/$/, '').toLowerCase()
    return parsed.protocol === 'https:' &&
      parsed.hostname === 'github.com' &&
      !parsed.port &&
      !parsed.username &&
      !parsed.password &&
      !parsed.search &&
      !parsed.hash &&
      path === expectedPath
      ? parsed.href
      : undefined
  } catch {
    return undefined
  }
}

function decodeInstructionKind(value: unknown): ContributedInstructionKind | undefined {
  return CONTRIBUTED_INSTRUCTION_KINDS.has(value as ContributedInstructionKind)
    ? (value as ContributedInstructionKind)
    : undefined
}

function decodeContributedInstruction(value: unknown): ContributedInstructionMetadata | undefined {
  if (!isPlainRecord(value)) return undefined

  const kind = decodeInstructionKind(value.kind)
  if (!kind) return undefined

  const label = boundedString(value.label)
  const ownerUrl = safeInstructionUrl(value.ownerUrl, kind, label)
  return {
    kind,
    ...(label ? {label} : {}),
    ...(ownerUrl ? {ownerUrl} : {}),
  }
}

/**
 * Decodes the browser-contributed per-turn instruction snapshot without
 * trusting wire metadata.
 */
export function decodeContributedInstructions(raw: unknown): ContributedInstructionsMetadata | undefined {
  try {
    if (!Array.isArray(raw) || raw.length > MAX_CONTRIBUTED_INSTRUCTIONS) return undefined
    if (JSON.stringify(raw).length > MAX_CONTRIBUTED_INSTRUCTIONS_METADATA_LENGTH) return undefined

    const decoded = raw
      .map(decodeContributedInstruction)
      .filter((row): row is ContributedInstructionMetadata => row !== undefined)
    return decoded.length > 0 ? decoded : undefined
  } catch {
    return undefined
  }
}

/** Reads the contributed-instruction snapshot from a turn's initiating message. */
export function extractTurnContributedInstructions(
  message: Turn['message'] | undefined,
): ContributedInstructionsMetadata | undefined {
  return decodeContributedInstructions(message?._meta?.[CONTRIBUTED_INSTRUCTIONS_META_KEY])
}

export function collectTurnActorIds(chatStates: Readonly<Record<string, ChatState>>): number[] {
  const actorIds = new Set<number>()
  for (const state of Object.values(chatStates)) {
    for (const turn of getTurns(state)) {
      const actor = extractTurnActor(turn.message)
      if (actor) actorIds.add(actor.githubId)
    }
  }
  return [...actorIds]
}
